import { Link } from 'react-router-dom'
import { appUrl } from '@astro/config'
// Scoped styles for the whole scene — every auth view's inputs, OTP boxes and
// animations are namespaced under `.login-scene`, which this component renders.
import '../../pages/LoginPage.css'

/**
 * Dual-window auth shell: cosmic billboard on the left, secure console on the
 * right. Shared by every auth screen (login / register / OTP / forgot password
 * / reset password) — the active view renders as children inside the console,
 * above the always-present legal footer.
 */
export default function AuthScene({ children }) {
  return (
    <div className="login-scene bg-astroDark text-white antialiased min-h-screen overflow-x-hidden flex flex-col">

      {/* MASTER DUAL WINDOW SPLIT SCENE */}
      <div className="w-full grid grid-cols-1 lg:grid-cols-12 min-h-screen lg:h-screen auto-rows-fr">

        {/* LEFT SIDE: COSMIC BILLBOARD */}
        <div className="relative lg:col-span-6 bg-linear-to-br from-astroDark via-[#3a0606] to-astroDark flex flex-col justify-between p-8 sm:p-12 lg:p-16 overflow-hidden lg:h-full border-b lg:border-b-0 lg:border-r border-goldPrimary/10">

          {/* Animated Background Layers */}
          <div className="absolute inset-0 pointer-events-none z-0" aria-hidden="true">
            <div className="absolute top-[-15%] left-[-10%] w-[70vw] h-[70vw] bg-cosmicRed/35 rounded-full login-aura" />
            <div className="absolute bottom-[-10%] right-[10%] w-[55vw] h-[55vw] bg-red-900/25 rounded-full login-aura" style={{ animationDelay: '-4s' }} />

            {/* Rotating Astrological Chakra Map */}
            <svg className="absolute bottom-[-15%] right-[-10%] w-[115%] md:w-[78%] aspect-square opacity-[0.14] text-goldPrimary login-chart" viewBox="0 0 200 200" fill="none" stroke="currentColor">
              <circle cx="100" cy="100" r="98" strokeWidth="0.6" />
              <circle cx="100" cy="100" r="80" strokeWidth="0.5" strokeDasharray="2 4" />
              <circle cx="100" cy="100" r="60" strokeWidth="0.5" />
              <path d="M100 2v196M2 100h196" strokeWidth="0.35" />
            </svg>

            {/* Dynamic Planetary Orbital Spheres */}
            <div className="absolute bottom-[-10%] right-[-5%] w-[110%] md:w-[70%] aspect-square z-10 login-chart-reverse">
              <div className="absolute top-[10%] left-[25%] w-4.5 h-4.5 rounded-full bg-linear-to-tr from-goldAccent to-goldPrimary opacity-90 shadow-[0_0_15px_rgba(245,158,11,0.7)]" />
              <div className="absolute top-[45%] left-[84%] w-4 h-4 rounded-full bg-slate-100 border border-slate-300 shadow-[0_0_10px_rgba(255,255,255,0.4)]" />
              <div className="absolute bottom-[20%] left-[15%] w-3.5 h-3.5 rounded-full bg-red-600 shadow-[0_0_12px_rgba(220,38,38,0.6)]" />
              <div className="absolute bottom-[40%] left-[80%] w-4.5 h-4.5 rounded-full bg-amber-950 border border-goldPrimary/60 flex items-center justify-center">
                <div className="w-6 h-1 border border-goldPrimary/40 rounded-full rotate-12 absolute" />
              </div>
            </div>

            {/* Floating Branded OM Particles */}
            <div className="absolute text-goldPrimary/25 font-royal font-bold text-4xl login-om-drift top-1/4 left-[15%] select-none">ॐ</div>
            <div className="absolute text-goldPrimary/15 font-royal text-6xl font-bold bottom-1/4 left-[8%] login-om-drift" style={{ animationDelay: '2.5s' }}>ॐ</div>

            <div className="absolute rounded-full bg-goldPrimary/25 login-aura" style={{ top: '45%', left: '65%', width: '5px', height: '5px', animationDuration: '9s' }} />
            <div className="absolute rounded-full bg-red-500/20 login-aura" style={{ bottom: '30%', left: '25%', width: '3px', height: '3px', animationDuration: '11s', animationDelay: '-3s' }} />
          </div>

          {/* Header Branding */}
          <div className="relative z-10 flex items-center gap-3 select-none">
            <div className="w-9 h-9 rounded-full bg-linear-to-tr from-goldAccent to-goldPrimary flex items-center justify-center text-astroDark font-bold text-base shadow-md">ॐ</div>
            <div className="flex flex-col">
              <span className="font-royal text-base sm:text-lg font-bold tracking-widest leading-none text-goldPrimary">ASTROLOGER RAJU</span>
              <span className="text-[11px] font-semibold tracking-[0.22em] text-white/40 uppercase mt-0.5">Vedic Wisdom Master</span>
            </div>
          </div>

          {/* Typography & Copy Block */}
          <div className="relative z-10 my-auto py-6 max-w-xl space-y-6 flex flex-col justify-center text-left mx-auto w-full px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-goldPrimary/40" />
              <span className="text-[11px] uppercase font-bold tracking-[0.35em] text-goldPrimary/70 font-royal">Trusted Mission Portal</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-royal leading-[1.12] text-[#FBF3E4]">
              Synchronize with <br />
              <span className="bg-linear-to-r from-goldPrimary via-amber-300 to-goldAccent bg-clip-text text-transparent font-semibold">Your Destiny</span>
            </h1>

            <p className="text-base text-white/50 font-light leading-relaxed max-w-md">
              Join the verified Vedic Astrology Mission gateway blueprint infrastructure. Access precision chart calculation systems, custom remedies, and tailored cosmic schedules.
            </p>

            {/* Value Indicators */}
            <div className="space-y-4 pt-6 border-t border-white/5 max-w-sm w-full">
              <div className="flex items-start gap-4 text-left">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-goldPrimary text-sm shrink-0">🛡️</div>
                <div>
                  <h4 className="text-sm font-semibold font-royal uppercase tracking-wider text-white/80">100% Secure System</h4>
                  <p className="text-xs text-white/40 mt-0.5 font-light">Your spiritual path data vectors remain encrypted and strictly confidential.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 text-left">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-goldPrimary text-sm shrink-0">✓</div>
                <div>
                  <h4 className="text-sm font-semibold font-royal uppercase tracking-wider text-white/80">Verified Lineage</h4>
                  <p className="text-xs text-white/40 mt-0.5 font-light">Authentic system predictions calculated directly using Maharaj Ji methodology tracks.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-10 h-2 select-none" />
        </div>

        {/* RIGHT SIDE: SECURE CONSOLE */}
        <div className="relative lg:col-span-6 bg-portalLight flex flex-col justify-center items-center p-6 sm:p-12 lg:p-16 lg:h-full overflow-y-auto">

          <div className="w-full max-w-xl relative py-4">

            {children}

            {/* Legal policies */}
            <div className="mt-6 pt-4 border-t border-stone-200 flex items-center justify-center gap-4 text-[11px] tracking-wider uppercase font-medium text-stone-400 select-none">
              <a href={appUrl("website", "privacy-policy")} target='_blank' className="hover:text-goldAccent transition-colors">Privacy Policy</a>
              <span>·</span>
              <a href={appUrl("website", "terms-of-service")} target='_blank' className="hover:text-goldAccent transition-colors">Terms of Service</a>
              <span>·</span>
              <a href={appUrl("website", "refund-policy")} target='_blank' className="hover:text-goldAccent transition-colors">Refund Policy</a>
            </div>

          </div>
        </div>

      </div>
    </div>
  )
}

/** Return navigation link — the views that want it render it above the footer. */
export const ReturnHomeLink = () => (
  <div className="mt-8 text-center select-none">
    <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase font-bold tracking-widest text-stone-400 hover:text-goldAccent transition-colors outline-none focus:text-goldAccent">
      <span>🏠</span> Return to Home
    </Link>
  </div>
)
